import { useState, useEffect, useCallback } from 'react';
import { EmojiMessage, LiveStreamEmoji } from '../services/live-stream-service';

interface UseLiveStreamEventsProps {
  streamId: string;
  chatToken?: string;
  enabled?: boolean;
}

interface UseLiveStreamEventsReturn {
  emojis: EmojiMessage[];
  sendLocalEmoji: (emoji: LiveStreamEmoji) => void;
  isConnected: boolean;
}

/**
 * Hook for live stream emoji display.
 * Shows emojis locally when sent. The backend broadcasts via IVS Chat SendEvent
 * which is received by the IVS player's timed metadata for future enhancement.
 * 
 * For now, emojis are displayed locally on the sender's screen immediately,
 * and the backend API handles persistence and IVS Chat broadcast.
 */
export function useLiveStreamEvents({
  streamId,
  chatToken,
  enabled = true,
}: UseLiveStreamEventsProps): UseLiveStreamEventsReturn {
  const [emojis, setEmojis] = useState<EmojiMessage[]>([]);

  // Clean up old emojis every second
  useEffect(() => {
    const interval = setInterval(() => {
      setEmojis(prev => prev.filter(e => Date.now() - e.timestamp < 4000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Add emoji locally for instant visual feedback
  const sendLocalEmoji = useCallback((emoji: LiveStreamEmoji) => {
    const localEmoji: EmojiMessage = {
      id: `local-${Date.now()}-${Math.random()}`,
      emoji,
      userId: 'self',
      timestamp: Date.now(),
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 20,
    };
    setEmojis(prev => [...prev.slice(-19), localEmoji]);
  }, []);

  return { emojis, sendLocalEmoji, isConnected: enabled };
}
